import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

type AttachmentListener = () => void;

const taskListeners = new Map<string, Set<AttachmentListener>>();

function subscribeTaskMessageAttachments(
  taskId: string,
  listener: AttachmentListener,
): () => void {
  let set = taskListeners.get(taskId);
  if (!set) {
    set = new Set();
    taskListeners.set(taskId, set);
  }
  set.add(listener);

  return () => {
    set?.delete(listener);
    if (set?.size === 0) taskListeners.delete(taskId);
  };
}

/** Notifica hooks/listeners para recarregar anexos após insert externo. */
export function invalidateTaskMessageAttachments(taskId?: string) {
  if (taskId) {
    taskListeners.get(taskId)?.forEach((listener) => listener());
    return;
  }
  taskListeners.forEach((listeners) => {
    listeners.forEach((listener) => listener());
  });
}

export interface AttachedMessage {
  id: string;
  body: string | null;
  from_user: string;
  created_at: string | null;
  conversation_id: string;
  metadata: Record<string, unknown> | null;
  deleted_at: string | null;
  sent_by_member?: { display_name: string | null } | null;
}

export interface TaskMessageAttachment {
  id: string;
  task_id: string;
  message_id: string;
  workspace_id: string;
  created_by_member_id: string | null;
  created_at: string;
  message: AttachedMessage | null;
  creator?: { display_name: string | null } | null;
}

const ATTACHMENT_SELECT = `
  id, task_id, message_id, workspace_id, created_by_member_id, created_at,
  creator:created_by_member_id(display_name),
  message:messages(
    id, body, from_user, created_at, conversation_id, metadata, deleted_at,
    sent_by_member:workspace_members!messages_sent_by_member_id_fkey(display_name)
  )
`;

interface UseTaskMessageAttachmentsOptions {
  /** Quando false, não busca (ex.: diálogo fechado ou subtarefa recolhida). */
  enabled?: boolean;
}

export function useTaskMessageAttachments(
  taskId: string | null,
  workspaceId: string | null,
  options: UseTaskMessageAttachmentsOptions = {},
) {
  const { enabled = true } = options;
  const [attachments, setAttachments] = useState<TaskMessageAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchAttachments = useCallback(async () => {
    if (!enabled || !taskId || !workspaceId) {
      setAttachments([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("workspace_task_message_attachments")
        .select(ATTACHMENT_SELECT)
        .eq("task_id", taskId)
        .eq("workspace_id", workspaceId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      setAttachments((data ?? []) as unknown as TaskMessageAttachment[]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao carregar mensagens anexadas",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [enabled, taskId, workspaceId, toast]);

  useEffect(() => {
    void fetchAttachments();
  }, [fetchAttachments]);

  useEffect(() => {
    if (!enabled || !taskId) return;
    return subscribeTaskMessageAttachments(taskId, () => {
      void fetchAttachments();
    });
  }, [enabled, taskId, fetchAttachments]);

  const attachMessage = useCallback(
    async (targetTaskId: string, messageId: string, memberId: string | null) => {
      if (!workspaceId) {
        throw new Error("Workspace inválido");
      }

      const { error } = await supabase.from("workspace_task_message_attachments").insert({
        task_id: targetTaskId,
        message_id: messageId,
        workspace_id: workspaceId,
        created_by_member_id: memberId,
      });

      if (error) {
        if (error.code === "23505") {
          throw new Error("Esta mensagem já está anexada a esta tarefa.");
        }
        throw error;
      }

      invalidateTaskMessageAttachments(targetTaskId);
    },
    [workspaceId],
  );

  const detachMessage = useCallback(
    async (attachmentId: string) => {
      const { error } = await supabase
        .from("workspace_task_message_attachments")
        .delete()
        .eq("id", attachmentId);

      if (error) throw error;
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    },
    [],
  );

  return {
    attachments,
    loading,
    refresh: fetchAttachments,
    attachMessage,
    detachMessage,
  };
}
